import { FC, Fragment, HTMLProps } from 'react';
import {
  StyledFieldset,
  StyledLegend,
  StyledRadios,
  StyledRadioLabel,
  StyledRadioInput,
  StyledDescription,
} from './Radio.styles';
import { ButtonSize } from '../Button';

export interface RadioItem {
  id: string;
  label: string;
  value: string;
}

export interface RadioProps
  extends Omit<HTMLProps<HTMLFieldSetElement>, 'value' | 'defaultValue'> {
  /** the label for the entire radio group */
  legend: string;
  /** whether or not to show the legend (it will be accessibly hidden if not) */
  legendVisible?: boolean;
  /** a list of radio items */
  items: RadioItem[];
  /** the shared name of the radio items (used when serializing form values) */
  name: string;
  /** an optional subtitle */
  description?: string;
  /** whether or not the radio group is disabled */
  disabled?: boolean;
  /** the inital value of the radio group (uncontrolled) */
  defaultValue?: string | null;
  /** the value of the radio group (controlled) */
  value?: string | null;
  /** The size of the button */
  $buttonSize?: ButtonSize;
  /** props to pass to the radio input elements */
  inputProps?: HTMLProps<HTMLInputElement>;
  /** props to pass to the radio label elements */
  labelProps?: HTMLProps<HTMLLabelElement>;
}

/**
Thin wrapper around a HTML input type='radio' element
*/
export const Radio: FC<RadioProps> = ({
  legend,
  items,
  name,
  description,
  disabled,
  labelProps,
  inputProps,
  value: controlledValue,
  defaultValue: initialValue,
  $buttonSize = ButtonSize.Standard,
  legendVisible = true,
  ...otherProps
}) => {
  return (
    <StyledFieldset {...otherProps}>
      <StyledLegend $visible={legendVisible}>{legend}</StyledLegend>
      {description && <StyledDescription>{description}</StyledDescription>}

      <StyledRadios data-testid="styled-radio" $disabled={disabled}>
        {items.map(({ id, label, value }) => (
          <Fragment key={id}>
            <StyledRadioInput
              type="radio"
              id={id}
              name={name}
              value={value}
              disabled={disabled}
              checked={controlledValue ? controlledValue === value : undefined}
              defaultChecked={initialValue ? initialValue === value : undefined}
              {...inputProps}
            />
            <StyledRadioLabel
              $buttonSize={$buttonSize}
              htmlFor={id}
              {...labelProps}
            >
              {label}
            </StyledRadioLabel>
          </Fragment>
        ))}
      </StyledRadios>
    </StyledFieldset>
  );
};

export default Radio;
